import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MfaChallengePanel from '../components/Mfa/MfaChallengePanel';
import { isMfaChallenge } from '../services/mfaService';
import { api } from '../services/api';
import { setAccessToken } from '../utils/authToken';
import { captureCsrfFromAuthPayload } from '../utils/csrf';
import { isFlashAdmin } from '../utils/adminAccess';
import { User } from '../types/user';

export const getPostLoginDestination = (user: User | null | undefined): string => {
  if (isFlashAdmin(user)) {
    return '/admin';
  }
  if (user?.role === 'INSURER') {
    return '/insurer-dashboard';
  }
  return '/tabletop-exercises';
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<{ mfaToken: string; setupRequired: boolean } | null>(null);

  const { completeMagicLinkAuth, user: authUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated && authUser) {
      navigate(getPostLoginDestination(authUser), { replace: true });
    }
  }, [authLoading, isAuthenticated, authUser, navigate]);

  const handleMagicLink = async () => {
    if (!email) {
      setError('Enter your email to receive a sign-in link.');
      return;
    }
    setError('');
    setMagicLinkLoading(true);
    setMagicLinkSent(false);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/v1/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setMagicLinkSent(true);
      } else {
        setError('Could not send sign-in link. Try again or use your password.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);

      const { data } = await api.post('/v1/auth/login', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (isMfaChallenge(data)) {
        setMfaChallenge({
          mfaToken: data.mfa_token,
          setupRequired: data.setup_required,
        });
        return;
      }

      captureCsrfFromAuthPayload(data);
      setAccessToken(data.access_token, data.expires_in);
      const user = await completeMagicLinkAuth(data);
      navigate(getPostLoginDestination(user), { replace: true });
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ??
        err.response?.data?.message ??
        err.message ??
        'Login failed. Please check your credentials.';
      const msg = typeof detail === 'string' ? detail : 'Login failed. Please check your credentials.';
      if (msg.includes('Email not verified') || msg.includes('Account not activated')) {
        setError('Please verify your email before logging in. Check your inbox for a verification link.');
      } else if (msg.toLowerCase().includes('under review')) {
        setError(
          'Your insurer application is under review. We will email you when your organization is approved.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/flashailogo.png"
            alt="Flash AI"
            className="w-auto h-20"
            onError={(e) => {
              // Fallback if logo doesn't exist
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-center text-gray-900">
          Welcome back
        </h2>
        <p className="mt-2 text-sm text-center text-gray-600">
          Sign in to your Flash AI™ account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
          {mfaChallenge ? (
            <MfaChallengePanel
              mfaToken={mfaChallenge.mfaToken}
              setupRequired={mfaChallenge.setupRequired}
              onError={setError}
              onComplete={async (tokens) => {
                setAccessToken(tokens.access_token, tokens.expires_in);
                const user = await completeMagicLinkAuth(tokens);
                navigate(getPostLoginDestination(user), { replace: true });
              }}
            />
          ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {magicLinkSent && (
              <div className="p-4 bg-green-50 rounded-md border border-green-200">
                <p className="text-sm text-green-800">
                  If your account is eligible, a one-time sign-in link has been sent to your email.
                </p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 rounded-md border border-red-200">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="relative mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block px-3 py-2 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="block px-3 py-2 pr-10 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <button
                  type="button"
                  className="flex absolute inset-y-0 right-0 items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="remember-me" className="block ml-2 text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="flex relative justify-center px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent group hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading && (
                  <svg className="mr-3 -ml-1 w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
          )}

          {!mfaChallenge && (
          <div className="mt-6 space-y-4">
            <div className="text-center">
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={magicLinkLoading || !email}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
              >
                {magicLinkLoading ? 'Sending link…' : 'Email me a sign-in link (insurance partners)'}
              </button>
            </div>
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                  Sign up here
                </Link>
              </span>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;